import React, { Fragment, useEffect, useState } from 'react';
import ResourceRow from './ResourceRow';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getUser } from '../../utils/tokens';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { filter, find, forEach } from 'rambda';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
	closestCorners,
	DndContext,
	DragOverlay,
	KeyboardSensor,
	MouseSensor,
	TouchSensor,
	useDroppable,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { useAuthDispatch } from '../../contexts/auth';
import { remove } from 'ramda';

const listUrl = clientId => `resource/v1/resources/clients/${clientId}`;
const sortUrl = (resourceId, idx) => `resource/v1/resources/sort/${resourceId}/position/${idx}`;
const categoryListUrl = clientId => `resource/v1/resource-categories/clients/${clientId}`;

const ResourceList = ({ resources, setResources, categories, setCategories }) => {
	const [isAdmin, setIsAdmin] = useState(false);
	const [clonedItems, setClonedItems] = useState(null);
	const [draggingRow, setDraggingRow] = useState(null);
	const [currentOver, setCurrentOver] = useState(null);
	const auth = useAuthDispatch();

	let navigate = useNavigate();

	const currentUser = getUser();

	const mouseSensor = useSensor(MouseSensor);
	const touchSensor = useSensor(TouchSensor);
	const keyboardSensor = useSensor(KeyboardSensor);
	const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

	useEffect(() => {
		if (currentUser.scope) {
			if (currentUser.scope.includes('admin')) {
				setIsAdmin(true);
			}
		}
	}, [currentUser.scope]);

	/**
	 *
	 * @param id
	 */
	function handleAddClick(id) {
		navigate('create');
	}

	function handleBack() {
		navigate('/');
	}

	const handleDragEnd = ({ active, over }) => {
		resetOverRow();
		if (active && over && active.id !== over.id) {
			const activeKeys = active.id.split(':');
			const categoryId = activeKeys[0];
			const resourceIdx = activeKeys[1];

			const overKeys = over.id.split(':');
			const overResourceIdx = overKeys[1];
			const overCategoryId = overKeys[0];

			const category = find(c => {
				return c.id.toString() === categoryId;
			}, categories);

			if (overCategoryId !== categoryId) {
				const overCategory = find(c => {
					return c.id.toString() === overCategoryId;
				}, categories);

				const oldIdx = category.resources.findIndex(item => item.idx.toString() === resourceIdx);
				let newIdx = overCategory.resources.findIndex(item => item.idx.toString() === overResourceIdx);

				if (newIdx < 0) {
					newIdx = 0;
				}

				const movedResource = category.resources[resourceIdx];
				overCategory.resources.splice(newIdx, 0, movedResource);

				// adjust idx's so there are no duplicates
				let i = 0;
				forEach(r => {
					r.idx = i++;
				}, overCategory.resources);

				category.resources = remove(oldIdx, oldIdx + 1, category.resources);

				axios({
					method: 'PATCH',
					url: sortUrl(movedResource.id, newIdx) + '?new-category=' + overCategoryId,
				}).then(
					resp => {
						category.resources = resp.data;
						let arrayCopy = Array.from(categories);
						setCategories(arrayCopy);
					},
					error => {
						console.log(error);
					},
				);
			} else {
				const oldIdx = category.resources.findIndex(item => item.idx.toString() === resourceIdx);
				const newIdx = category.resources.findIndex(item => item.idx.toString() === overResourceIdx);
				const sortedResources = arrayMove(category.resources, oldIdx, newIdx);
				const res = category.resources[oldIdx];
				category.resources = sortedResources;

				axios({
					method: 'PATCH',
					url: sortUrl(res.id, newIdx),
				}).then(
					resp => {
						category.resources = resp.data;
						let arrayCopy = Array.from(categories);
						setCategories(arrayCopy);
					},
					error => {
						console.log(error);
					},
				);
			}
		}
	};

	const handleDragStart = ({ active }) => {
		setClonedItems(categories);

		let activeId = active.id.split(':')[0];
		activeId = parseInt(activeId);
		const activeCat = find(c => c.id === activeId, categories);
		let resId = active.id.split(':')[1];
		resId = parseInt(resId);
		const activeRes = find(r => r.idx === resId, activeCat.resources);
		setDraggingRow(activeRes);
	};

	const handleDragCancel = () => {
		setCategories(clonedItems);
		setDraggingRow(null);
		setCurrentOver(null);
		resetOverRow();
	};

	const handleDragOver = ({ active, over }) => {
		let activeId = active.id.split(':')[0];
		let overId = over.id.split(':')[0];

		if (activeId !== overId) {
			if (currentOver && over.id !== currentOver) {
				resetOverRow();
			}

			const row = document.getElementById(over.id);
			if (row) {
				const child = row.firstChild;
				child.classList.add('border-red-900');
				child.classList.add('border-2');
				setCurrentOver(over.id);
			}
		} else if (currentOver) {
			resetOverRow();
		}
	};

	const resetOverRow = () => {
		if (currentOver) {
			const overRow = document.getElementById(currentOver);
			const child = overRow.firstChild;
			child.classList.remove('border-red-900');
			child.classList.remove('border-2');
			setCurrentOver(null);
		}
	};
	const handleDragMove = e => {};

	const getSortItems = () => {
		let items = [];
		forEach(c => {
			let resp = c.resources.map(item => {
				return c.id + ':' + item.idx.toString();
			});

			items.push(...resp);
		}, categories);

		console.log(items);
		return items;
	};
	/**
	 *
	 */
	return (
		<div className="py-4 px-4 sm:px-6 lg:px-8">
			{!auth.isIframe() && (
				<div className="pb-4" onClick={handleBack}>
					<span className="text-indigo-600">
						<ArrowUturnLeftIcon className="h-6 w-6 text-indigo-600" />
					</span>
				</div>
			)}

			<div className="sm:flex sm:items-center">
				<div className="sm:flex-auto">
					{isAdmin && (
						<h1 className="text-xl font-semi-bold text-gray-900">
							Global Resources - Organize and share content to keep everyone in the know
						</h1>
					)}
					{!isAdmin && (
						<h1 className="text-xl font-semi-bold text-gray-900">
							Organize and share content to keep everyone in the know
						</h1>
					)}
				</div>
				<div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
					<button
						onClick={() => {
							handleAddClick();
						}}
						type="button"
						className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto">
						Add Resource
					</button>
				</div>
			</div>

			<div className="-mx-4 mt-8 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:-mx-6 md:mx-0 md:rounded-lg">
				<DndContext
					modifiers={[restrictToVerticalAxis]}
					collisionDetection={closestCorners}
					onDragEnd={handleDragEnd}
					onDragMove={handleDragMove}
					onDragStart={handleDragStart}
					onDragCancel={handleDragCancel}
					onDragOver={handleDragOver}>
					<table className="min-w-full divide-y divide-gray-300">
						<thead className="bg-gray-200">
							<tr>
								<th
									scope="col"
									className="py-3.5 pl-4 pr-3 text-left text-sm font-semi-bold text-gray-900 sm:pl-6">
									Name
								</th>
								<th
									scope="col"
									className="hidden px-3 py-3.5 text-left text-sm font-semi-bold text-gray-900 lg:table-cell">
									Type
								</th>
								<th
									scope="col"
									className="hidden px-3 py-3.5 text-left text-sm font-semi-bold text-gray-900 lg:table-cell">
									Enabled
								</th>
								{isAdmin && (
									<th
										scope="col"
										className="hidden px-3 py-3.5 text-left text-sm font-semi-bold text-gray-900 lg:table-cell">
										Global
									</th>
								)}
								<th scope="col"></th>
							</tr>
						</thead>
						<tbody className="divide-y divide-gray-200 bg-white">
							{resources.map(resource => (
								<ResourceRow
									key={resource.id}
									resource={resource}
									resources={resources}
									setResources={setResources}
								/>
							))}

							{categories.map(category => (
								<Fragment key={category.id}>
									<SortableContext
										id={category.id}
										items={category.resources.map(item => {
											return category.id + ':' + item.idx.toString();
										})}
										sensors={sensors}
										strategy={verticalListSortingStrategy}>
										<DroppableCategoryHeader id={category.id + ':' + -1} category={category} />
										{category.resources.map(resource => (
											<ResourceRow key={resource.id} resource={resource} category={category.id} />
										))}
									</SortableContext>
								</Fragment>
							))}
						</tbody>
					</table>

					<DragOverlay>
						{draggingRow ? (
							<ResourceRow isDragging={true} key={draggingRow.id} resource={draggingRow} />
						) : null}
					</DragOverlay>
				</DndContext>
			</div>
		</div>
	);
};

/**
 *
 * @returns {JSX.Element}
 * @constructor
 */
const LoadingResourceList = () => {
	return <div>Loading resources</div>;
};

/**
 *
 * @returns {JSX.Element|*}
 * @constructor
 */
const ResourceListPage = ({ admin }) => {
	const [resources, setResources] = useState(null);
	const [categories, setCategories] = useState(null);
	const [isLoaded, setIsLoaded] = useState(false);

	const params = useParams();
	const currentUser = getUser();

	useEffect(() => {
		const getCategories = async () => {
			let url = categoryListUrl(currentUser.clientIdNum);
			if (!admin) {
				url = url + '?global=true';
			}
			return await axios
				.get(url)
				.then(response => {
					const sorted = response.data.sort((a, b) => {
						return a.idx > b.idx ? 1 : -1;
					});

					const catList = forEach(c => {
						c.resources = [];
					}, sorted);

					setCategories(catList);
				})
				.catch(error => {
					console.log(error);
				});
		};

		getCategories().then();
	}, []);

	useEffect(() => {
		const getResources = async () => {
			return await axios
				.get(listUrl(currentUser.clientIdNum))
				.then(response => {
					forEach(c => {
						c.resources = filter(r => {
							return r.resourceCategory ? r.resourceCategory.id === c.id : false;
						}, response.data);

						c.resources.sort((a, b) => {
							return a.idx > b.idx ? 1 : -1;
						});
					}, categories);

					setResources(response.data);
				})
				.catch(error => {
					console.log(error);
				});
		};

		if (categories) {
			getResources().then();
		}
	}, [categories]);

	useEffect(sortResourcesForDisplay, [resources]);

	function sortResourcesForDisplay() {
		if (resources && categories) {
			setIsLoaded(true);
		}
	}

	if (!isLoaded) {
		return <LoadingResourceList />;
	}

	if (isLoaded) {
		const noCategory = filter(r => {
			return !r.resourceCategory;
		}, resources);

		return (
			<ResourceList
				resources={noCategory}
				setResources={setResources}
				categories={categories}
				setCategories={setCategories}
			/>
		);
	}
};

const DroppableCategoryHeader = ({ id, columns = 1, category, ...props }) => {
	const { setNodeRef } = useDroppable({
		id,
	});

	return (
		<tr id={id} className="border-t border-gray-200" ref={setNodeRef}>
			<td colSpan={5} scope="colgroup" className="bg-gray-50 py-2 text-left text-sm text-gray-500 sm:px-3">
				Category - {category.title}
			</td>
		</tr>
	);
};

export default ResourceListPage;
